import { useEffect } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'

import { sessionQuery } from '#admin/features/auth/queries.ts'
import { manifestQuery } from '#admin/features/tenant/queries.ts'
import { reloadThemeStylesheet } from '#admin/features/theme/stylesheet.ts'
import { AppShell } from '#admin/components/app-shell.tsx'
import { ShellSkeleton } from '#admin/components/shell-skeleton.tsx'

/**
 * The authentication boundary — a **pathless layout**, so it guards without appearing in any
 * URL.
 *
 * The session and the manifest are fetched in the loader rather than in the component, so the
 * navigation is already correct on first paint. A shell that rendered its links after the
 * manifest arrived would flash every module and then remove the ones this tenant has not
 * bought, which reads as a bug and briefly advertises what the platform sells.
 *
 * There is no `$tenantSlug` segment here, unlike the router guide's example. The Host **is**
 * the tenant: this console is deployed per edition, and switching editions means going to
 * another hostname rather than another route.
 */
export const Route = createFileRoute('/_authenticated')({
  pendingComponent: ShellSkeleton,
  loader: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery())

    if (session.user === null) {
      // `throw redirect`, not a render-time redirect: this runs before anything paints, so a
      // signed-out visitor never sees the console frame at all.
      throw redirect({ to: '/login' })
    }

    await context.queryClient.ensureQueryData(manifestQuery())
  },
  component: AuthenticatedLayout,
})

function AuthenticatedLayout() {
  const { data: manifest } = useSuspenseQuery(manifestQuery())
  // Already in the cache — the loader above awaited it — so this is a read, not a second fetch.
  const { data: session } = useSuspenseQuery(sessionQuery())
  const tenantId = manifest?.tenant.id

  /**
   * Ask for the theme again once the signed-in edition is known, and again if it changes.
   *
   * `index.html` links `/v1/tenant/theme.css`, and the browser asks for it once, at document
   * load. On a host that names no tenant (the console's own hostname, or the computer's address
   * on a phone) the API resolves the theme from the session. A load that came before the
   * session, or a session that changed edition, leaves the link holding the fallback palette.
   * `login.tsx` covers a sign-in; this covers every other way into the console. The ETag makes
   * the request nearly free when nothing changed.
   */
  useEffect(() => {
    if (tenantId !== undefined) reloadThemeStylesheet()
  }, [tenantId])

  return (
    <AppShell manifest={manifest ?? undefined} session={session}>
      <Outlet />
    </AppShell>
  )
}
